import { Injectable, inject } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppConfigService } from '@ccfx/shared-lib';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

// ── Types ─────────────────────────────────────────────────────────────────────

export type HubConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * Typed payload sent by the server's `ReceiveNotification` event.
 * All fields are optional except `Type` — extend as new notification types are added.
 */
export interface HubNotification {
  Type: string;
  Message?: string;
  [key: string]: unknown;
}

/** Typed payload sent by the server's `InventoryUpdated` event. */
export interface HubInventoryUpdate {
  VaultId: number;
  Currency: string;
  TransactionType?: string;
  Message?: string;
}

/** Typed payload sent by the server's `RatesUpdated` event. */
export interface HubRatesUpdate {
  rates: unknown[];
  timestamp: string;
}

// ── Internal message envelope ─────────────────────────────────────────────────

interface HubMessage {
  event: string;
  data: unknown;
}

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Singleton SignalR hub service for the backoffice application.
 *
 * ## Architecture
 * - A **single** `HubConnection` is shared across all features.
 * - Features subscribe to typed events via `on<T>(eventName)`.
 * - New topics are supported zero-config: just call `on('MyNewEvent')` and the
 *   service registers the handler automatically, even before `connect()` is called.
 * - The service handles token refresh, automatic reconnection, and status broadcasting.
 *
 * ## Usage
 * ```ts
 * // In any service or store:
 * private readonly hub = inject(SignalRHubService);
 *
 * // Subscribe to a typed event
 * this.hub.on<HubNotification>('ReceiveNotification').subscribe(evt => {
 *   if (evt.Type === 'TierApprovalRequired') { ... }
 * });
 *
 * // Start the connection (typically called once after auth is confirmed)
 * this.hub.connect();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SignalRHubService {
  private readonly configService = inject(AppConfigService);
  private readonly msalInstance = inject(MSAL_INSTANCE) as IPublicClientApplication;

  private connection: HubConnection | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manualDisconnect = false;

  /** Routes all server-sent messages to type-filtered observables. */
  private readonly messages$ = new Subject<HubMessage>();

  /** Tracks event names registered before the connection was created. */
  private readonly pendingEvents = new Set<string>();

  private readonly _status$ = new BehaviorSubject<HubConnectionStatus>('disconnected');

  /** Emits whenever the connection status changes. */
  readonly status$: Observable<HubConnectionStatus> = this._status$.pipe(distinctUntilChanged());

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Subscribe to a named server-sent SignalR event.
   *
   * Safe to call **before** `connect()` — events will start flowing once the
   * connection is established. The handler is automatically re-registered after
   * a reconnect.
   *
   * @param eventName - Server-side SignalR method name (e.g. `'ReceiveNotification'`)
   * @returns Observable that emits once per server push for this event.
   */
  on<T>(eventName: string): Observable<T> {
    this.ensureEventRegistered(eventName);
    void this.connect();
    return this.messages$.pipe(
      filter((msg) => msg.event === eventName),
      map((msg) => msg.data as T),
    );
  }

  /**
   * Establish the hub connection.
   * Idempotent — subsequent calls are no-ops when already connected or connecting.
   * Should be called once after authentication succeeds.
   */
  async connect(): Promise<void> {
    if (
      this.connection?.state === HubConnectionState.Connected ||
      this.connection?.state === HubConnectionState.Connecting ||
      this.connection?.state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    this.manualDisconnect = false;

    let config;
    try {
      config = this.configService.getConfig();
    } catch {
      // Config not yet loaded — retry shortly to establish hub once config is ready.
      this._status$.next('disconnected');
      this.scheduleReconnect();
      return;
    }

    // In mock/dev mode there is no real API — skip connection entirely.
    if (config.enableMocks) {
      return;
    }

    const hubUrl = `${config.apiBaseUrl}/hubs/notifications`;
    this._status$.next('connecting');
    this.logConnection('Connecting to notifications hub.', {
      hubUrl,
      strategy: 'websocket-only-first-then-fallback',
    });

    try {
      // First try WebSocket-only fast path.
      this.connection = this.createConnection(hubUrl, true);
      await this.connection.start();
      this.clearReconnectTimer();
      this._status$.next('connected');
      this.logConnection('Connected to notifications hub via WebSockets.', {
        hubUrl,
        transport: 'WebSockets',
      });
    } catch (wsErr) {
      console.warn(
        '[SignalRHubService] WebSocket-only connect failed, trying negotiated fallback.',
        wsErr,
      );
      await this.connection?.stop();

      try {
        // Fallback to negotiated transport for environments where WebSockets are blocked.
        this.connection = this.createConnection(hubUrl, false);
        await this.connection.start();
        this.clearReconnectTimer();
        this._status$.next('connected');
        this.logConnection('Connected to notifications hub using negotiated transport fallback.', {
          hubUrl,
          transport: 'Negotiated',
        });
      } catch (fallbackErr) {
        this._status$.next('disconnected');
        console.warn('[SignalRHubService] SignalR connection failed after fallback.', fallbackErr);
        this.logConnection('Failed to connect to notifications hub after fallback.', {
          hubUrl,
          error: fallbackErr,
        });
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Stop and dispose of the hub connection.
   * The service can be reconnected by calling `connect()` again.
   */
  disconnect(): void {
    this.manualDisconnect = true;
    this.clearReconnectTimer();
    this.connection?.stop();
    this.connection = null;
    this._status$.next('disconnected');
    this.logConnection('Disconnected from notifications hub by client request.');
  }

  private createConnection(hubUrl: string, websocketOnly: boolean): HubConnection {
    const builder = new HubConnectionBuilder().withUrl(hubUrl, {
      ...(websocketOnly
        ? {
            // Fast path when WebSockets are available.
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
          }
        : {
            // Allow server/client to negotiate best available transport.
            skipNegotiation: false,
          }),
      accessTokenFactory: () => this.acquireToken(),
    });

    const connection = builder.withAutomaticReconnect().configureLogging(LogLevel.None).build();

    connection.onreconnecting(() => {
      this._status$.next('reconnecting');
      this.logConnection('SignalR connection lost, attempting to reconnect.');
    });

    connection.onreconnected(() => {
      this.clearReconnectTimer();
      this._status$.next('connected');
      this.logConnection('SignalR connection re-established.');
    });

    connection.onclose(() => {
      this._status$.next('disconnected');
      this.logConnection('SignalR connection closed. Scheduling reconnect if allowed.');
      this.scheduleReconnect();
    });

    // Bind any events that were registered before connect() was called.
    for (const event of this.pendingEvents) {
      connection.on(event, (data: unknown) => {
        this.messages$.next({ event, data });
      });
    }

    return connection;
  }

  private scheduleReconnect(): void {
    if (this.manualDisconnect || this.reconnectTimer !== null) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, 5000);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private logConnection(message: string, details?: Record<string, unknown>): void {
    if (details) {
      console.info('[SignalRHubService]', message, details);
      return;
    }
    console.info('[SignalRHubService]', message);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Register a new event name. If the connection already exists the handler is
   * bound immediately; otherwise it is queued for binding after `connect()`.
   */
  private ensureEventRegistered(eventName: string): void {
    if (this.pendingEvents.has(eventName)) return;
    this.pendingEvents.add(eventName);

    if (this.connection) {
      this.bindEvent(eventName);
    }
  }

  /**
   * Attach a handler for `eventName` on the live connection and pipe data
   * through the shared `messages$` subject.
   */
  private bindEvent(eventName: string): void {
    if (!this.connection) return;
    this.connection!.on(eventName, (data: unknown) => {
      this.logConnection('Hub event received.', { event: eventName, data });
      this.messages$.next({ event: eventName, data });
    });
  }

  /**
   * Acquire a fresh Bearer token from MSAL for the SignalR handshake.
   * Returns an empty string on failure to allow the connection attempt to
   * proceed (the server will return a 401 and the caller can handle it).
   */
  private async acquireToken(): Promise<string> {
    let config;
    try {
      config = this.configService.getConfig();
    } catch {
      return '';
    }

    const account =
      this.msalInstance.getActiveAccount() ?? this.msalInstance.getAllAccounts()[0] ?? null;

    if (!account) return '';

    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes: config.auth.scopes,
        account,
      });
      return result.accessToken;
    } catch {
      return '';
    }
  }
}
